import { QStack, QText, useFeatureFlag } from "@qualio/ui-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReviewApproval } from "./ReviewApproval";
import {
  isDesignElementRequirement,
  isDesignElementRisk,
  isDesignElementTestCase,
  ReleaseReport,
  ReviewRecord,
  FeatureFlags,
} from "@design-controls/types";
import DOMPurify from "dompurify";
import { ReportsTable } from "../../../components/OverviewTable/ReportsTable";
import { RequirementTable } from "../../../components/OverviewTable/RequirementTable";
import { RisksTable } from "../../../components/OverviewTable/RisksTable";
import { TestCaseTable } from "../../../components/OverviewTable/TestCaseTable";
import { LinkedDocumentsTable } from "../../../components/OverviewTable/LinkedDocumentsTable";

type Props = {
  canApprove: boolean;
  data: ReviewRecord;
  reportData?: ReleaseReport;
  isComplete: boolean;
  isLoading: boolean;
  hasOutdatedDesignElements?: boolean;
};

export const ReviewDetail: React.FC<Props> = ({
  canApprove,
  data,
  reportData,
  isComplete,
  isLoading,
  hasOutdatedDesignElements,
}) => {
  const { t } = useTranslation();
  const features = useFeatureFlag();
  const showLinkedVersions = Boolean(
    features[FeatureFlags.VERSION_TRACELINK_UI],
  );
  const showVersionCompare = Boolean(
    features[FeatureFlags.VERSION_TRACELINK_UI] &&
      features[FeatureFlags.MAJOR_MINOR_VERSION_UI],
  );
  const requirements = useMemo(() => {
    return (data?.qualityItems ?? []).filter(isDesignElementRequirement);
  }, [data]);
  const testCases = useMemo(() => {
    return (data?.qualityItems ?? []).filter(isDesignElementTestCase);
  }, [data]);
  const risks = useMemo(() => {
    return (data?.qualityItems ?? []).filter(isDesignElementRisk);
  }, [data]);

  return (
    <QStack gap="24px">
      {canApprove && isComplete && <ReviewApproval />}
      <QStack gap="8px" className="ck-content document">
        <QText fontSize="sm" fontWeight={600} color="gray.900">
          {t("reviews.overview.sections.description")}
        </QText>
        {data.description && (
          <QText
            fontSize={14}
            style={{ wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(data.description),
            }}
          />
        )}
      </QStack>

      <ReportsTable
        data={reportData}
        isComplete={isComplete}
        isLoading={isLoading}
      />
      <RequirementTable
        requirements={requirements}
        underReview={hasOutdatedDesignElements}
        versioned
        showLinkedVersions={showLinkedVersions}
        showVersionCompare={showVersionCompare}
      />
      <RisksTable
        risks={risks}
        underReview={hasOutdatedDesignElements}
        versioned
        showLinkedVersions={showLinkedVersions}
        showVersionCompare={showVersionCompare}
      />
      <TestCaseTable
        testCases={testCases}
        underReview={hasOutdatedDesignElements}
        versioned
        showLinkedVersions={showLinkedVersions}
        showVersionCompare={showVersionCompare}
      />
      <LinkedDocumentsTable documents={data.linkedDocuments} />
    </QStack>
  );
};
